/**
 * Kiosk Liveness Heartbeat
 *
 * A 1-second monotonic tick measures the largest gap between expected ticks
 * WHILE THE PAGE IS VISIBLE — i.e. the worst main-thread/renderer stall (a
 * freeze) in each ~30s window. Every window is POSTed to /api/v1/kiosk-heartbeat
 * with maxStallMs + a visibility summary.
 *
 * This finally separates a genuine renderer freeze (screen on, ticks stop ⇒
 * large maxStallMs while visible) from a normal Fully-Kiosk screen-off pause
 * (visibility='hidden', not counted). It gives sub-60s freeze visibility from
 * OUTSIDE the renderer — no ADB and no cross-origin-isolation required (unlike
 * performance.memory). Runs only in kiosk mode in production builds.
 */

import { isKioskModeEnabled } from './kiosk-stability';

const TICK_MS = 1000;
const REPORT_MS = 30_000;
const API_ENDPOINT = '/api/v1/kiosk-heartbeat';
const DEVICE_ID_KEY = 'kiosk_device_id';

export type VisibilitySummary = 'visible' | 'hidden' | 'mixed';

/**
 * Drift beyond the expected tick interval. A value > ~0 means the main thread
 * was blocked: it took (elapsed) to fire a timer scheduled for (tickMs).
 * Clamped at 0 (timers can fire a hair early; negative drift is not a stall).
 */
export function tickStallMs(elapsedMs: number, tickMs: number): number {
  return Math.max(0, Math.round(elapsedMs - tickMs));
}

/** Summarize page visibility observed across a window. */
export function summarizeVisibility(sawVisible: boolean, sawHidden: boolean): VisibilitySummary {
  if (sawVisible && sawHidden) return 'mixed';
  if (sawHidden) return 'hidden';
  return 'visible';
}

export interface HeartbeatTracker {
  /** Record a tick; `visible` = page was visible at this moment. */
  tick(visible: boolean): void;
  /**
   * Page just became visible after being hidden. Resets the tick baseline so a
   * screen-off pause is NOT misattributed as a visible main-thread stall — the
   * first visible tick after wake would otherwise see the whole dark gap.
   */
  markVisible(): void;
  /** Read the window's worst visible stall + visibility summary, then reset. */
  snapshotAndReset(): { maxStallMs: number; visibility: VisibilitySummary };
}

/**
 * Pure, injectable state machine for the heartbeat — `now` is a monotonic clock
 * (performance.now in prod). Extracted so the visibility/stall logic is unit
 * testable without timers or the DOM.
 */
export function createHeartbeatTracker(now: () => number, tickMs: number = TICK_MS): HeartbeatTracker {
  let lastTick = now();
  let maxStallVisibleMs = 0;
  let sawVisible = false;
  let sawHidden = false;

  return {
    tick(visible: boolean): void {
      const t = now();
      const stall = tickStallMs(t - lastTick, tickMs);
      lastTick = t;
      if (visible) {
        sawVisible = true;
        if (stall > maxStallVisibleMs) maxStallVisibleMs = stall;
      } else {
        sawHidden = true;
      }
    },
    markVisible(): void {
      lastTick = now();
    },
    snapshotAndReset(): { maxStallMs: number; visibility: VisibilitySummary } {
      const out = { maxStallMs: maxStallVisibleMs, visibility: summarizeVisibility(sawVisible, sawHidden) };
      maxStallVisibleMs = 0;
      sawVisible = false;
      sawHidden = false;
      return out;
    },
  };
}

/** Stable per-device id so heartbeats from one tablet are groupable server-side. */
function getDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `dev-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return `ephemeral-${Date.now()}`;
  }
}

interface HeartbeatPayload {
  deviceId: string;
  maxStallMs: number;
  visibility: VisibilitySummary;
  uptimeMs: number;
  intervalMs: number;
  url: string;
  userAgent: string;
}

function postHeartbeat(payload: HeartbeatPayload): void {
  try {
    const body = JSON.stringify(payload);
    // Prefer sendBeacon so a heartbeat still flushes during unload/reload.
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(API_ENDPOINT, blob)) return;
    }
    fetch(API_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // best-effort — never let telemetry break the app
  }
}

export function initKioskHeartbeat(): (() => void) | null {
  // Dev builds skip it (avoid console/network noise during HMR).
  if (import.meta.env.DEV) return null;
  // Only the kiosk display reports; regular browser visitors do not.
  if (!isKioskModeEnabled()) return null;

  const deviceId = getDeviceId();
  const loadAt = performance.now();
  let windowStart = performance.now();
  const tracker = createHeartbeatTracker(() => performance.now());

  const tickTimer = window.setInterval(() => {
    tracker.tick(document.visibilityState === 'visible');
  }, TICK_MS);

  // Reset the baseline when the page becomes visible so a screen-off gap is not
  // counted as a visible stall (the boundary tick would otherwise mis-attribute it).
  const onVisibility = () => {
    if (document.visibilityState === 'visible') tracker.markVisible();
  };
  document.addEventListener('visibilitychange', onVisibility);

  const reportTimer = window.setInterval(() => {
    const now = performance.now();
    const snap = tracker.snapshotAndReset();
    postHeartbeat({
      deviceId,
      maxStallMs: snap.maxStallMs,
      visibility: snap.visibility,
      uptimeMs: Math.round(now - loadAt),
      intervalMs: Math.round(now - windowStart),
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
    windowStart = now;
  }, REPORT_MS);

  return () => {
    clearInterval(tickTimer);
    clearInterval(reportTimer);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
